import type { ModuleLoggerAPI } from '@ad-lucy/utils';
import type { StateUpdater } from 'preact/hooks';
import {
  createContext,
  type Dispatch,
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { VideoJsPlayer } from 'video.js';
import { LoggerService } from '../LoggerService';

type Marker = {
  time: number;
  text?: string;
  overlayText?: string;
  class?: string;
};

export type VideoJsPlayerMarkers = VideoJsPlayer & {
  markers?: {
    getMarkers: () => Marker[];
    add: (markers: Marker[]) => void;
    reset: (markers: Marker[]) => void;
    remove: (indexArray: number[]) => void;
    removeAll: () => void;
    destroy: () => void;
    next: () => void;
    prev: () => void;
    updateTime: (force: boolean) => void;
  };
};

export type VideoPlayerContextType = {
  videoPlayer: VideoJsPlayerMarkers | undefined;
  setVideoPlayer: Dispatch<StateUpdater<VideoJsPlayerMarkers | undefined>>;
  visibleVideoRect: DOMRect | undefined;
};

export const VideoPlayerContext = createContext<VideoPlayerContextType>(
  {} as VideoPlayerContextType,
);
export const useVideoPlayer = (): VideoPlayerContextType => useContext(VideoPlayerContext);

const logger = (): ModuleLoggerAPI =>
  LoggerService.getLogger().getModuleLogger(VideoPlayerProvider.name);

export function VideoPlayerProvider({
  children,
}: {
  readonly children: ReactElement;
}): ReactElement {
  const [videoPlayer, setVideoPlayer] = useState<VideoJsPlayerMarkers>();
  const [visibleVideoRect, setVisibleVideoRect] = useState<DOMRect>();

  useEffect(() => {
    if (!videoPlayer) return;

    const resizeListener = () => {
      const aspectRatio = videoPlayer.videoWidth() / videoPlayer.videoHeight();
      if (!aspectRatio) {
        logger().warn('Unable to determine aspect ratio of video');
        return;
      }

      const playerRect = videoPlayer.tech().el().getBoundingClientRect();
      const videoHeight = playerRect.width / aspectRatio;

      setVisibleVideoRect(
        new DOMRect(
          playerRect.x,
          playerRect.y + (playerRect.height - videoHeight) / 2,
          playerRect.width,
          videoHeight,
        ),
      );
    };

    videoPlayer.on('playerresize', resizeListener);
    videoPlayer.on('loadeddata', resizeListener);
    return () => {
      videoPlayer.off('playerresize', resizeListener);
      videoPlayer.off('loadeddata', resizeListener);
    };
  }, [videoPlayer]);

  const videoPlayerContextValue = useMemo(
    () => ({
      videoPlayer,
      setVideoPlayer,
      visibleVideoRect,
    }),
    [videoPlayer, visibleVideoRect],
  );

  return (
    <VideoPlayerContext.Provider value={videoPlayerContextValue}>
      {children}
    </VideoPlayerContext.Provider>
  );
}
